import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { TokenPayload } from "@models/finish-sign-up";
import { UserInfo } from "@models/user";
import {
  CountriesService,
  StateSubdivisionOption,
} from "@services/countries.service";
import { SearchPostalCodeService } from "@services/search-postal-code.service";
import { SnackBarService } from "@services/snack-bar.service";
import { splitPhoneNumber } from "@shared/helpers/phoneNumber";
import {
  cityValidator,
  cpfValidator,
  nameValidator,
  phoneNumberValidator,
} from "@shared/validatos";
import { passwordsMatchValidator } from "@shared/validatos/passwords-match.validator";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { DdiSelectComponent } from "../../../../../shared/components/ddi-select/ddi-select.component";
import { countriesList } from "../../../../../utils/constants/countries.constant";
import { SignupRequestBody } from "./../../../../services/types/signup-request.type";

@Component({
    selector: "app-finish-sign-up-form",
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonComponent,
        DdiSelectComponent,
        NgxMaskDirective,
    ],
    providers: [provideNgxMask()],
    templateUrl: "./finish-sign-up-form.component.html",
    styleUrl: "./finish-sign-up-form.component.scss"
})
export class FinishSignUpFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tokenPayload?: TokenPayload;
  @Input() userData?: UserInfo;
  @Input() isLoading = false;

  hidePassword = true;
  hideConfirm = true;

  statesOptions: StateSubdivisionOption[] = [];
  countriesList = countriesList;

  private destroy$ = new Subject<void>();

  private readonly formBuilder = inject(FormBuilder);
  private readonly searchPostalCodeService = inject(SearchPostalCodeService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly countriesService = inject(CountriesService);

  signupForm = this.formBuilder.group(
    {
      name: ["", [Validators.required, Validators.minLength(3), nameValidator]],
      cpf: ["", [cpfValidator]],
      ddi: ["+55", [Validators.required]],
      phone: ["", [Validators.required, phoneNumberValidator]],
      country: ["BR", [Validators.required]],
      state: ["", [Validators.required]],
      city_name: ["", [Validators.required, cityValidator]],
      street: ["", [Validators.required]],
      number: ["", [Validators.required]],
      district: ["", [Validators.required]],
      postal_code: ["", [Validators.required]],
      additional_information: [""],
      email: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required, Validators.minLength(6)]],
      confirmPassword: ["", [Validators.required]],
      activationMethod: ["email"],
      identity_card: [""],
    },
    { validators: [passwordsMatchValidator] },
  );

  controls = this.signupForm.controls;

  isBrazilianCostumer = false;
  isLoadingCep = false;

  @Output() formSubmitClick = new EventEmitter<SignupRequestBody>();

  ngOnInit() {
    this.subscribeToCountryInput();
    this.subscribeToCepInput();
    this.loadSubdivisions(this.signupForm.value.country!);
    console.log(this.userData);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tokenPayload"]) {
      const current = changes["tokenPayload"].currentValue;

      this.handleTokenPayload(current);
    }

    if (changes["userData"]) {
      this.userData = changes["userData"].currentValue;

      this.handleUserDataChanges();
    }
  }

  onSubmit() {
    this.signupForm.markAllAsTouched();

    if (this.signupForm.invalid) {
      this.snackbarService.openSnackBar("Dados inválidos", "Fechar", "error");
      return;
    }

    const {
      name,
      ddi,
      phone,
      email,
      state,
      city_name,
      street,
      number,
      district,
      postal_code,
      additional_information,
      password,
      confirmPassword,
      cpf,
      country,
      identity_card,
    } = {
      ...this.signupForm.value,
    };

    // Validação do identity_card para usuários estrangeiros
    if (
      !this.isBrazilianCostumer &&
      (!identity_card || identity_card.trim() === "")
    ) {
      this.snackbarService.openSnackBar(
        "Passport number é obrigatório para usuários estrangeiros",
        "Fechar",
        "error",
      );
      return;
    }

    const nomeLimpo = name!
      .replace(/\s{2,}/g, " ")
      .trim()
      .split(" ");

    const firstName = nomeLimpo[0];
    const lastName = nomeLimpo?.slice(1)?.join(" ");

    const signupRequest: SignupRequestBody = {
      user: {
        email: email,
        password: password,
        password_confirmation: confirmPassword,
        name: firstName,
        last_name: lastName,
        roles: [""],
        phone_number: `${ddi}${phone}`,
        country,
        identity_card: !this.isBrazilianCostumer ? identity_card : undefined,
        cpf: this.isBrazilianCostumer
          ? cpf
            ? cpf!.replace(/[^0-9]/g, "")
            : ""
          : undefined,
        birth_date: "1983-07-25",
        address_attributes: {
          state,
          street,
          number,
          district,
          city_name,
          postal_code,
          country,
          additional_information: additional_information,
        },
      },
    } as SignupRequestBody;

    this.formSubmitClick.emit(signupRequest);
  }

  get isConfirmPasswordInvalid() {
    return (
      (this.signupForm.get("confirmPassword")?.invalid &&
        this.signupForm.get("confirmPassword")?.touched) ||
      this.signupForm.hasError("passwordsMismatch")
    );
  }

  private handleTokenPayload(payload: TokenPayload | undefined): void {
    if (!payload) return;
    this.signupForm.patchValue(payload);
  }

  private handleUserDataChanges() {
    if (!this.userData) {
      return;
    }

    const { ddi, nationalNumber } = splitPhoneNumber(
      this.userData.phone_number,
    );

    this.signupForm.patchValue(
      {
        ...this.userData,
        ...this.userData.address,
        ddi: ddi,
        phone: nationalNumber,
        name: `${this.userData.name} ${this.userData.last_name}`,
      },
      {
        emitEvent: false,
      },
    );

    this.clearPasswordValidators();
  }

  private clearPasswordValidators() {
    const { password, confirmPassword } = this.signupForm.controls;
    this.signupForm.removeValidators(passwordsMatchValidator);
    password.clearValidators();
    confirmPassword.clearValidators();
    this.signupForm.updateValueAndValidity();
    password.disable();
    confirmPassword.disable();
  }

  private sanitizeCep(value: unknown): string {
    return String(value ?? "").replace(/\D/g, "");
  }

  private verifyCep = (value: unknown): value is string => {
    if (!this.isBrazilianCostumer) {
      return false;
    }
    const cep = this.sanitizeCep(value);
    return cep.length === 8 && !/^0+$/.test(cep);
  };

  private subscribeToCepInput() {
    const { postal_code } = this.signupForm.controls;

    postal_code.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        map((cepValue) => this.sanitizeCep(cepValue)),
        distinctUntilChanged(),
        filter(this.verifyCep),
        tap(() => {
          this.isLoadingCep = true;
        }),
        switchMap((cep) =>
          this.searchPostalCodeService.getPostalCode(cep).pipe(
            finalize(() => {
              this.isLoadingCep = false;
            }),
          ),
        ),
      )
      .subscribe((cepResponse) => {
        if (!cepResponse) {
          return;
        }
        this.signupForm.patchValue(
          {
            street: cepResponse.logradouro,
            district: cepResponse.bairro,
            city_name: cepResponse.localidade,
            state: cepResponse.uf,
            additional_information: cepResponse.complemento,
          },
          {
            emitEvent: false,
          },
        );
      });
  }

  private subscribeToCountryInput() {
    const { country } = this.signupForm.controls;

    country.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.signupForm.get("country")?.value),
      )
      .subscribe((country) => {
        this.setCountryRelatedValidations(country ?? "BR");
        this.loadSubdivisions(country ?? "BR");
      });
  }

  private setCountryRelatedValidations(country?: string) {
    const { cpf, identity_card, state, ddi, phone } = this.signupForm.controls;
    this.isBrazilianCostumer = country === "BR";
    if (this.isBrazilianCostumer) {
      identity_card.reset();
      identity_card.clearValidators();
      cpf.addValidators(Validators.required);
      state.setValidators(Validators.required);
    } else {
      cpf.reset();
      cpf.removeValidators(Validators.required);
      identity_card.addValidators(Validators.required);
      state.addValidators(Validators.maxLength(6));
    }
    cpf.updateValueAndValidity({ emitEvent: false });
    identity_card.updateValueAndValidity({ emitEvent: false });
    state.updateValueAndValidity({ emitEvent: false });
    this.signupForm.updateValueAndValidity();

    const countryDdiCode = countriesList.find(
      (countryEntry) => countryEntry.acronym === country,
    )?.code;

    if (countryDdiCode && !phone.value) {
      ddi.setValue(countryDdiCode);
    }
  }

  private loadSubdivisions(countryCode: string) {
    const { state } = this.signupForm.controls;

    this.statesOptions = this.countriesService.getSubdivisions(countryCode);
    if (!state.value) {
      return;
    }

    const hasCurrentState =
      this.statesOptions.findIndex(
        (stateOption) => stateOption.code === state.value,
      ) >= 0;
    if (!hasCurrentState) {
      state.setValue(null);
    }
  }
}
